"use client";

import React from "react";
import { ArrowRight, Building2, CircleCheck as CheckCircle2, House as Home, Layers, MapPin, ShieldCheck, Umbrella, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const coverageAreas = [{
  id: 1,
  label: "Town-centre commercial properties"
}, {
  id: 2,
  label: "Office & retail developments"
}, {
  id: 3,
  label: "Residential streets & heritage housing"
}, {
  id: 4,
  label: "Refurbishment & mixed-use buildings"
}, {
  id: 5,
  label: "Roofing repairs & solar installations"
}];
const residentialScopes = [{
  id: 1,
  title: "Roof repairs & renewals"
}, {
  id: 2,
  title: "House extensions & loft conversions"
}, {
  id: 3,
  title: "Chimney access & rebuilds"
}, {
  id: 4,
  title: "Exterior rendering, painting & repointing"
}];
const commercialScopes = [{
  id: 1,
  title: "Main contractors & housing developers"
}, {
  id: 2,
  title: "Commercial refurbishments & fit-outs"
}, {
  id: 3,
  title: "Occupied office & retail sites"
}, {
  id: 4,
  title: "Pavement licenses & pedestrian gantries"
}];
const roofingTradeLinks = [{
  id: 1,
  title: "Roofing Scaffolding",
  description: "Independent scaffolding and high-level working platforms tailored for roofers.",
  to: "/RoofingScaffoldingPage",
  iconName: "wrench"
}, {
  id: 2,
  title: "Temporary Roofs",
  description: "Complete weather protection and shrink-wrap canopies for major structural works.",
  to: "/TemporaryRoofsPage",
  iconName: "umbrella"
}, {
  id: 3,
  title: "Edge Protection",
  description: "Handrails, debris netting, and fall prevention systems for site safety.",
  to: "/EdgeProtectionPage",
  iconName: "shield"
}];
const nearbyAreas = [{
  id: 1,
  name: "Wandsworth",
  to: "/scaffolding-in-wandsworth",
  description: "Domestic refurbishments, high-street retail, and commercial access scaffolding."
}, {
  id: 2,
  name: "Clapham",
  to: "/scaffolding-in-clapham",
  description: "Period townhouse extensions, roof renewals, and main contractor scaffolding."
}, {
  id: 3,
  name: "Kingston upon Thames",
  to: "/scaffolding-in-kingston-upon-thames",
  description: "Thames-side commercial developments, shopfronts, and domestic scaffolding."
}];
export default function Coverage() {
  const shouldReduceMotion = useReducedMotion();
  const motionVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="richmondscaffoldingcoverage" data-nav="light" className="w-full bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-border text-foreground">
            Richmond & South West London Coverage (TW9 & TW10)
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Scaffolding coverage across Richmond
          </h2>
          <p className="mt-4 font-default text-base text-muted-foreground sm:text-lg">
            Safe, compliant access structures for town-centre commercial properties, office
            developments, residential homes, and refurbishment projects throughout the TW9 and TW10
            postcode districts in Richmond upon Thames.
          </p>
        </div>

        {/* Coverage Highlights Strip */}
        <div className="mt-10 rounded-xl border border-border bg-card p-6 shadow-xs">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Typical Richmond project types covered across TW9 & TW10
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {coverageAreas.map((area, i) => <div key={area.id} data-index={i} className="flex items-center gap-2 rounded-lg bg-muted/50 p-2.5 text-sm font-medium text-foreground">
                <CheckCircle2 className="size-4 shrink-0 text-primary" />
                <span className="leading-snug">{area.label}</span>
              </div>)}
          </div>
        </div>

        {/* Two Main Subsections: Residential vs Commercial */}
        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Residential Subsection */}
          <motion.div initial={shouldReduceMotion ? false : "hidden"} whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={motionVariants}>
            <Card className="flex h-full flex-col justify-between border-border bg-card shadow-sm">
              <CardHeader>
                <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Home className="size-6" />
                </div>
                <CardTitle className="mt-3 font-default text-2xl font-bold text-card-foreground">
                  Residential scaffolding in Richmond
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Safe and tidy domestic scaffolding designed for Richmond homeowners, local builders,
                  and specialist trades across TW9 and TW10 working on period or modern properties.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-2.5">
                  {residentialScopes.map((scope, i) => <div key={scope.id} data-index={i} className="flex items-start gap-2.5 text-sm text-card-foreground">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span>{scope.title}</span>
                    </div>)}
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-3 pt-2 sm:flex-row">
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/ResidentialScaffoldingPage">
                    Residential Scaffolding
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="ghost" className="w-full sm:w-auto">
                  <Link to="/ChimneyScaffoldingPage">
                    Chimney Scaffolding
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Commercial Subsection */}
          <motion.div initial={shouldReduceMotion ? false : "hidden"} whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={motionVariants}>
            <Card className="flex h-full flex-col justify-between border-border bg-card shadow-sm">
              <CardHeader>
                <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Building2 className="size-6" />
                </div>
                <CardTitle className="mt-3 font-default text-2xl font-bold text-card-foreground">
                  Commercial scaffolding in Richmond
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Robust, fully audited access structures managed under NASC and CHAS standards
                  for high-footfall and occupied commercial environments across TW9 & TW10.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-2.5">
                  {commercialScopes.map((scope, i) => <div key={scope.id} data-index={i} className="flex items-start gap-2.5 text-sm text-card-foreground">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span>{scope.title}</span>
                    </div>)}
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-3 pt-2 sm:flex-row">
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/CommercialScaffoldingPage">
                    Commercial Scaffolding
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="ghost" className="w-full sm:w-auto">
                  <Link to="/MainContractorsDevelopersPage">
                    Main Contractors & Developers
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <div className="mt-16 rounded-2xl border border-border bg-card p-6 shadow-md sm:p-8">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="flex items-center gap-2 text-primary">
                <Layers className="size-5" />
                <span className="text-xs font-bold uppercase tracking-wider">Specialist Systems</span>
              </div>
              <h3 className="mt-2 font-default text-2xl font-bold text-card-foreground">
                Roofing & trade access
              </h3>
              <p className="mt-1 font-default text-sm text-muted-foreground sm:text-base">
                Purpose-engineered solutions designed for roofing specialists, solar installers, and
                exterior contractors across Richmond.
              </p>
            </div>
            <Button asChild className="shrink-0">
              <Link to="/ContactRequestQuotePage">
                Request a Site Survey
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <Separator className="my-6" />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {roofingTradeLinks.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col justify-between rounded-xl border border-border bg-background p-5 transition-[border-color,box-shadow] hover:border-primary/50 hover:shadow-xs">
                <div>
                  <div className="mb-3 flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    {item.iconName === "wrench" && <Wrench className="size-5" />}
                    {item.iconName === "umbrella" && <Umbrella className="size-5" />}
                    {item.iconName === "shield" && <ShieldCheck className="size-5" />}
                  </div>
                  <h4 className="font-default text-base font-semibold text-foreground">
                    {item.title}
                  </h4>
                  <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
                <div className="mt-4 pt-3">
                  <Button asChild variant="link" className="h-auto p-0 font-semibold text-primary">
                    <Link to={item.to}>
                      Explore {item.title}
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </div>
              </div>)}
          </div>
        </div>

        {/* Nearby Areas Covered */}
        <div className="mt-16 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <div className="flex items-center gap-2 text-primary">
                <MapPin className="size-5" />
                <span className="text-xs font-bold uppercase tracking-wider">Nearby Locations</span>
              </div>
              <h3 className="mt-2 font-default text-2xl font-bold text-card-foreground">
                Nearby areas we cover
              </h3>
              <p className="mt-1 font-default text-sm text-muted-foreground">
                Operating across South West London alongside our Richmond (TW9 & TW10) operations.
              </p>
            </div>
            <Button asChild variant="outline" className="shrink-0">
              <Link to="#">
                View all areas
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <Separator className="my-6" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {nearbyAreas.map((area, i) => <div key={area.id} data-index={i} className="flex flex-col justify-between rounded-xl border border-border bg-background p-5 transition-[border-color,box-shadow] hover:border-primary/50 hover:shadow-xs">
                <div>
                  <h4 className="font-default text-lg font-semibold text-foreground">
                    Scaffolding in {area.name}
                  </h4>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                    {area.description}
                  </p>
                </div>
                <div className="mt-4 pt-2">
                  <Button asChild variant="link" className="h-auto p-0 font-semibold text-primary">
                    <Link to={area.to}>
                      Explore {area.name}
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}